import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import {
  useActivityPopupPriority,
  useIsActivityQualificationAutoEnabled,
  useIsHomeDisplayReady,
  useDisplayItems,
  useActivities,
} from "@/modules/homeDisplay/hooks";
import { ActivityType, TimingType } from "@/modules/homeDisplay/types";
import { ROUTES } from "@/types/routes";
import type { RechargeOfferPopupResult } from "./useRechargeOfferPopup.types";
import RechargeOffer from "../components/RechargeOffer/RechargeOffer";
import { GroupedRechargeOffer } from "../components/GroupedRecharge/GroupedRechargeOffer";
import {
  getMinPositiveGroupedRechargeCountdownMs,
  isGroupedRechargeTaskFloatingFixedId,
} from "../groupedRechargeTaskFloatingId";
import { isGroupedBonusPopupPending } from "../groupedRechargeBonusFields";
import {
  coerceIdString,
  loadGroupedRechargeSeenTaskIds,
  saveGroupedRechargeSeenTaskIds,
} from "../groupedRechargeClaimReadStorage";

const RechargeOfferContent: React.FC<{
  onComplete: () => void;
  taskData?: any;
}> = ({ onComplete, taskData }) => {
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    onComplete();
  };

  return (
    <Suspense fallback={null}>
      <RechargeOffer
        visible={visible}
        onClose={handleClose}
        taskData={taskData}
      />
    </Suspense>
  );
};

// GROUPED_RECHARGE_TASK 专用弹窗内容
const GroupedRechargeOfferContent: React.FC<{
  onComplete: () => void;
  onRead?: () => void;
  taskData?: any;
}> = ({ onComplete, onRead, taskData }) => {
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    onComplete();
    onRead?.();
  };

  return (
    <Suspense fallback={null}>
      <GroupedRechargeOffer
        visible={visible}
        onClose={handleClose}
        taskData={taskData}
      />
    </Suspense>
  );
};

const RECHARGE_OFFER_CONFIG = POPUP_CONFIGS.RECHARGE_OFFER;

const getNextMidnight = (): number => {
  const now = new Date();
  const nextMidnight = new Date(now);
  nextMidnight.setHours(24, 0, 0, 0);
  return nextMidnight.getTime();
};

// 单个充值任务弹窗注册组件
interface TaskPopupRegistrationContext {
  task: any;
  priority: number;
  isConfigReady: boolean;
  userInfo: any;
  onRegisterTrigger: (taskId: string, trigger: () => void) => void;
  /** 区分渲染组件：old = 旧充值任务，grouped = 分组充值任务 */
  prioritySource: "old" | "grouped";
  /** 分组充值弹窗关闭时触发，用于刷新红叹号状态 */
  onGroupedRechargeRead?: () => void;
}

type RechargeOfferTaskEntry = {
  task: any;
  displayItem: any;
  prioritySource: "old" | "grouped";
};

const TaskPopupRegistration: React.FC<TaskPopupRegistrationContext> = ({
  task,
  priority,
  isConfigReady,
  userInfo,
  onRegisterTrigger,
  prioritySource,
  onGroupedRechargeRead,
}) => {
  const taskId = task.taskId;
  const popupId = `recharge-task-${taskId}`;
  const groupedBonusEligible =
    prioritySource === "grouped" && isGroupedBonusPopupPending(task);
  const isAutoEnabledOld = useIsActivityQualificationAutoEnabled(
    ActivityType.RECHARGE_PROMOTION
  );
  const isAutoEnabledGrouped = useIsActivityQualificationAutoEnabled(
    ActivityType.GROUPED_RECHARGE_TASK
  );
  const isAutoEnabled =
    prioritySource === "grouped" ? isAutoEnabledGrouped : isAutoEnabledOld;
  const { trigger } = useQueuedPopup({
    popupId,
    priority,
    shouldShow: () => {
      if (!isConfigReady) return false;
      if (prioritySource === "grouped") {
        return Number(task.rewardAmount ?? 0) > 0 || groupedBonusEligible;
      }
      return Number(task.rewardAmount ?? 0) > 0;
    },
    renderContent: onComplete =>
      prioritySource === "grouped" ? (
        <GroupedRechargeOfferContent
          onComplete={onComplete}
          onRead={onGroupedRechargeRead}
          taskData={task}
        />
      ) : (
        <RechargeOfferContent onComplete={onComplete} taskData={task} />
      ),
    showOnce: true,
    showOnceGranularity: "day",
    allowManualTrigger: true,
    autoTrigger: isAutoEnabled,
    dependencies: [
      isConfigReady,
      task.rewardAmount,
      task.bonusPopupPending,
      task.popupItems,
      task.popupRewardAmount,
      isAutoEnabled,
    ],
    allowedPaths: undefined,
    blockedPaths: [
      "/game",
      ROUTES.LOGIN,
      ROUTES.REGISTER,
      ROUTES.RECHARGE,
      ROUTES.WITHDRAW,
    ],
    userBinding: true,
    getUserId: () => userInfo?.userId,
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });

  // 注册trigger到父组件
  useEffect(() => {
    onRegisterTrigger(taskId, trigger);
  }, [taskId, trigger, onRegisterTrigger]);

  return null;
};

// 所有任务弹窗注册容器组件
const AllTaskPopupsRegistration: React.FC<{
  tasks: RechargeOfferTaskEntry[];
  priorityOld: number;
  priorityGrouped: number;
  isConfigReady: boolean;
  userInfo: any;
  onRegisterTrigger: (taskId: string, trigger: () => void) => void;
  onGroupedRechargeRead?: () => void;
}> = ({
  tasks,
  priorityOld,
  priorityGrouped,
  isConfigReady,
  userInfo,
  onRegisterTrigger,
  onGroupedRechargeRead,
}) => {
  return (
    <>
      {tasks.map(({ task, prioritySource }) => {
        const key = task.taskId || "";
        const priority =
          prioritySource === "grouped" ? priorityGrouped : priorityOld;
        return (
          <TaskPopupRegistration
            key={key}
            task={task}
            priority={priority}
            isConfigReady={isConfigReady}
            userInfo={userInfo}
            onRegisterTrigger={onRegisterTrigger}
            prioritySource={prioritySource}
            onGroupedRechargeRead={
              prioritySource === "grouped" ? onGroupedRechargeRead : undefined
            }
          />
        );
      })}
    </>
  );
};

export const useRechargeOfferPopup = (): RechargeOfferPopupResult => {
  const { data: userInfo } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();
  const displayItems = useDisplayItems();
  const activities = useActivities();
  const [offerEndAt, setOfferEndAt] = useState<number>(getNextMidnight);
  const [badgeRefreshKey, setBadgeRefreshKey] = useState(0);

  // 获取活动配置中的 priority，不存在则使用默认值（旧充值任务）
  const priorityOld = useActivityPopupPriority(
    ActivityType.RECHARGE_PROMOTION,
    RECHARGE_OFFER_CONFIG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );
  // 分组充值任务在 popupTimings 中单独配置 sort
  const priorityGrouped = useActivityPopupPriority(
    ActivityType.GROUPED_RECHARGE_TASK,
    RECHARGE_OFFER_CONFIG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );

  // 提取有效的充值任务（RECHARGE_TASK_OLD，逻辑保持不变）
  const oldRechargeTaskEntries = useMemo((): RechargeOfferTaskEntry[] => {
    if (!activities?.RECHARGE_TASK_OLD?.list || !displayItems) {
      return [];
    }

    const tasks = activities.RECHARGE_TASK_OLD.list;
    const floatingItems = displayItems.filter(
      (item: any) =>
        item.position === "FLOATING_ICON" &&
        item.sourceType === "ACTIVITY" &&
        item.activityType === "RECHARGE_TASK_OLD"
    );

    const matched: RechargeOfferTaskEntry[] = [];

    for (const displayItem of floatingItems) {
      const task = tasks.find(
        (t: any) => t.taskId === (displayItem as any).activityId
      );
      if (task) {
        matched.push({ task, displayItem, prioritySource: "old" });
      }
    }
    return matched.sort(
      (a, b) =>
        ((b.displayItem as any).sort || 0) - ((a.displayItem as any).sort || 0)
    );
  }, [activities, displayItems]);

  // GROUPED_RECHARGE_TASK：合并为单条弹窗；list 与 popup 语义见 groupedRechargeBonusFields.ts
  const groupedRechargeTaskEntries = useMemo((): RechargeOfferTaskEntry[] => {
    if (!activities?.GROUPED_RECHARGE_TASK?.list || !displayItems) {
      return [];
    }

    const tasks = activities.GROUPED_RECHARGE_TASK.list;
    const grt = activities.GROUPED_RECHARGE_TASK as Record<string, unknown>;
    const floatingItems = displayItems.filter(
      (item: any) =>
        (item.position === "FLOATING_ICON" ||
          item.position === "LEFT_BOTTOM_FLOATING_ICON") &&
        item.sourceType === "ACTIVITY" &&
        item.activityType === "GROUPED_RECHARGE_TASK"
    );

    const fixedItems = floatingItems.filter((item: any) =>
      isGroupedRechargeTaskFloatingFixedId((item as any).activityId)
    );

    // 收集所有匹配的任务（保留原始字段供 GroupedRechargeOffer 解析）
    const matchedTasks: any[] = [];

    if (fixedItems.length > 0) {
      for (const t of tasks) {
        matchedTasks.push({ ...t, taskId: t.taskGroupId });
      }
    } else {
      for (const displayItem of floatingItems) {
        const task = tasks.find(
          (t: any) => t.taskGroupId === (displayItem as any).activityId
        );
        if (task) {
          matchedTasks.push({ ...task, taskId: task.taskGroupId });
        }
      }
    }

    if (matchedTasks.length === 0) return [];

    const totalRewardAmount = matchedTasks.reduce(
      (sum, t) => sum + Number(t.rewardAmount ?? 0),
      0
    );
    const displayForSort = fixedItems.length > 0 ? fixedItems : floatingItems;
    const topDisplayItem = displayForSort.reduce((top: any, item: any) =>
      (item.sort || 0) > (top.sort || 0) ? item : top
    );

    return [
      {
        task: {
          taskId: "grouped-all",
          tasks: matchedTasks,
          rewardAmount: totalRewardAmount,
          bonusPopupPending: grt?.bonusPopupPending,
          popupItems: grt?.popupItems,
          popupRewardAmount: grt?.popupRewardAmount,
          list: grt?.list ?? matchedTasks,
        },
        displayItem: topDisplayItem,
        prioritySource: "grouped",
      },
    ];
  }, [activities, displayItems]);

  // 所有 GROUPED_RECHARGE_TASK 浮窗 activityId 集合，用于触发合并弹窗
  // 注：过滤已完成任务由后台负责（completed tasks 不会出现在 displayItems 中）
  const groupedActivityIds = useMemo((): Set<string> => {
    if (!displayItems) return new Set();
    return new Set(
      displayItems
        .filter(
          (item: any) =>
            (item.position === "FLOATING_ICON" ||
              item.position === "LEFT_BOTTOM_FLOATING_ICON") &&
            item.sourceType === "ACTIVITY" &&
            item.activityType === "GROUPED_RECHARGE_TASK"
        )
        .map((item: any) => item.activityId)
        .filter(Boolean)
    );
  }, [displayItems]);

  const rechargeTasks = useMemo(() => {
    return [...oldRechargeTaskEntries, ...groupedRechargeTaskEntries].sort(
      (a, b) =>
        ((b.displayItem as any).sort || 0) - ((a.displayItem as any).sort || 0)
    );
  }, [oldRechargeTaskEntries, groupedRechargeTaskEntries]);

  // 用于存储所有任务的 trigger 函数
  const triggersMap = useRef<Map<string, () => void>>(new Map());

  // 注册trigger的回调
  const handleRegisterTrigger = useCallback(
    (taskId: string, trigger: () => void) => {
      triggersMap.current.set(taskId, trigger);
    },
    []
  );

  // 判断是否应该显示
  const shouldShow = rechargeTasks.length > 0;

  // 根据 taskId 获取对应任务的触发器
  // 所有 GROUPED_RECHARGE_TASK 图标（无论 activityId）都映射到合并弹窗 "grouped-all"
  const getTriggerByTaskId = useCallback(
    (taskId: string) => {
      if (groupedActivityIds.has(taskId)) {
        return () => {
          const triggerFn = triggersMap.current.get("grouped-all");
          if (triggerFn) triggerFn();
        };
      }
      const task = rechargeTasks.find(t => t.task.taskId === taskId);
      if (!task) return undefined;
      return () => {
        const triggerFn = triggersMap.current.get(taskId);
        if (triggerFn) triggerFn();
      };
    },
    [rechargeTasks, groupedActivityIds]
  );

  // 更新倒计时结束时间
  useEffect(() => {
    if (userInfo?.userId) {
      setOfferEndAt(getNextMidnight());
    }
  }, [userInfo?.userId]);

  useEffect(() => {
    const now = Date.now();
    if (now >= offerEndAt) {
      setOfferEndAt(getNextMidnight());
    }
  }, [offerEndAt]);

  const remainingMs = Math.max(0, offerEndAt - Date.now());

  const groupedRechargeRemainingMs = useMemo(() => {
    const entry = groupedRechargeTaskEntries.find(
      t => t.task?.taskId === "grouped-all"
    );
    return getMinPositiveGroupedRechargeCountdownMs(entry?.task?.tasks);
  }, [groupedRechargeTaskEntries]);

  /**
   * 从合并任务中提取任务指纹列表（用于红叹号新任务判断）。
   * 使用 "taskGroupId:rechargeAmount:rewardAmount" 复合 key：
   *   - taskGroupId 相同但金额变化（任务内容更新）时视为新任务
   *   - 若 taskGroupId 为空则退回到 activityId，金额仍参与指纹
   */
  const currentGroupedTaskIds = useMemo((): string[] => {
    const tasks = groupedRechargeTaskEntries[0]?.task?.tasks as
      | any[]
      | undefined;
    if (tasks && tasks.length > 0) {
      const ids = tasks
        .map((t: any) => {
          const baseId = coerceIdString(t.taskId ?? t.taskGroupId ?? t.id);
          const recharge = Number(
            t.minSingleRechargeAmount ??
              t.rechargeAmount ??
              t.requiredAmount ??
              0
          );
          const reward = Number(t.rewardAmount ?? 0);
          // 复合指纹：ID + 充值门槛 + 奖励金额，任意字段变化即视为新任务
          return baseId
            ? `${baseId}:${recharge}:${reward}`
            : `${recharge}:${reward}`;
        })
        .filter(Boolean);
      if (ids.length > 0) return ids;
    }
    // 兜底：用 displayItems 里的 activityId（与 taskGroupId 对齐，肯定有值）
    return [...groupedActivityIds];
  }, [groupedRechargeTaskEntries, groupedActivityIds]);

  // 分组充值浮标红叹号：当前任务集合中存在用户未见过的任务 ID 时显示（localStorage 持久化）
  const shouldShowGroupedBadge = useMemo(() => {
    const userId = userInfo?.userId;
    if (!userId) {
      console.log("[GRT Badge] skip: no userId");
      return false;
    }
    if (groupedRechargeTaskEntries.length === 0) {
      console.log("[GRT Badge] skip: no entries");
      return false;
    }
    console.log("[GRT Badge] currentGroupedTaskIds:", currentGroupedTaskIds);
    // 有任务但 ID 全部无法提取时，兜底显示叹号（宁可多显示，不漏显示）
    if (currentGroupedTaskIds.length === 0) {
      console.log("[GRT Badge] fallback true: no IDs extracted");
      return true;
    }
    const seen = loadGroupedRechargeSeenTaskIds(userId);
    console.log("[GRT Badge] seen task IDs:", [...seen]);
    const hasNew = currentGroupedTaskIds.some(id => !seen.has(id));
    console.log("[GRT Badge] hasNew:", hasNew);
    return hasNew;
  }, [
    userInfo?.userId,
    groupedRechargeTaskEntries,
    currentGroupedTaskIds,
    badgeRefreshKey,
  ]);

  const handleGroupedRechargeRead = useCallback(() => {
    const userId = userInfo?.userId;
    if (userId) {
      console.log("[GRT Badge] saving seen task IDs:", currentGroupedTaskIds);
      saveGroupedRechargeSeenTaskIds(userId, currentGroupedTaskIds);
    }
    setBadgeRefreshKey(prev => prev + 1);
  }, [userInfo?.userId, currentGroupedTaskIds]);

  // 创建弹窗注册组件
  const PopupRegistrationComponent = useCallback(() => {
    return (
      <AllTaskPopupsRegistration
        tasks={rechargeTasks}
        priorityOld={priorityOld}
        priorityGrouped={priorityGrouped}
        isConfigReady={isConfigReady}
        userInfo={userInfo}
        onRegisterTrigger={handleRegisterTrigger}
        onGroupedRechargeRead={handleGroupedRechargeRead}
      />
    );
  }, [
    rechargeTasks,
    priorityOld,
    priorityGrouped,
    isConfigReady,
    userInfo,
    handleRegisterTrigger,
    handleGroupedRechargeRead,
  ]);

  return {
    shouldShow,
    remainingMs,
    groupedRechargeRemainingMs,
    rechargeTasks,
    PopupRegistrationComponent,
    getTriggerByTaskId,
    shouldShowGroupedBadge,
  };
};
